import { useState, useEffect, useMemo, useCallback } from 'react';
import type { FuelCaptureToggleableSetting, HighnoteTransactionEvent } from 'API';
import { ERPPostingStatus, FlexCodeTransactionStatus, HighnoteTransactionStatus } from 'API';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';

// ─── Constants ────────────────────────────────────────────────────────────────

export const FUEL_MCCS = ['5541', '5542'] as const;

// ─── Pure utility functions ───────────────────────────────────────────────────
// These are standalone exports (no React/Redux) and also re-exported from the
// hook for convenience so callers only need one import.

/**
 * Calculate cost per gallon in cents.
 * @param amountCents  Transaction approved amount in cents
 * @param gallonsRaw   Gallons as stored value (× 1000, e.g. 10 gallons = 10000)
 * @param isPending    True when transaction status is APPROVED (pending/not yet cleared)
 * @returns cents | null (pending) | 0 (invalid inputs)
 */
export function calculateCostPerGallon(
  amountCents: number,
  gallonsRaw: number | null | undefined,
  isPending: boolean
): number | null {
  if (isPending) return null;
  if (!gallonsRaw || gallonsRaw <= 0 || amountCents <= 0) return 0;
  return Math.round((amountCents * 1000) / gallonsRaw);
}

/**
 * Detect whether a stored cost per gallon was manually entered (differs from auto-calculated).
 */
export function detectIsManualCost(
  storedCostCents: number | null | undefined,
  amountCents: number,
  gallonsRaw: number | null | undefined,
  isPending: boolean
): boolean {
  if (storedCostCents == null) return false;
  // If gallons are missing we can't auto-calculate, so treat any stored cost as manual.
  if (!gallonsRaw || gallonsRaw <= 0) return true;
  const autoCost = calculateCostPerGallon(amountCents, gallonsRaw, isPending);
  // Normalize: treat null as 0 unless pending (where null is valid/expected)
  const normalizedStored = storedCostCents ?? (isPending ? null : 0);
  const normalizedAuto = autoCost ?? (isPending ? null : 0);
  return normalizedStored !== normalizedAuto;
}

/**
 * Return true if the transaction is in a final state (posted, bypassed, or flex-approved).
 * Uses `!= null` for agaveExpenseId to catch both null and undefined.
 */
export function isTransactionFinalState(
  transaction: Pick<
    HighnoteTransactionEvent,
    'erpBypassStatus' | 'agaveExpenseId' | 'erpPostingStatus' | 'flexCodeTransactionStatus'
  >
): boolean {
  if (transaction.erpBypassStatus === true) return true;
  if (transaction.agaveExpenseId != null) return true;
  if (transaction.erpPostingStatus === ERPPostingStatus.COMPLETED) return true;
  if (transaction.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED) return true;
  return false;
}

// ─── Hook types ───────────────────────────────────────────────────────────────

export interface CheckMissingFieldsParams {
  merchantCategoryCode: string | null | undefined;
  vehicleId: string | null | undefined;
  gallons: number | null | undefined;
  odometerReading: number | null | undefined;
}

export interface CheckMissingFieldsResult {
  isFuelTransaction: boolean;
  missingVehicleId: boolean;
  missingGallons: boolean;
  missingOdometer: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Provides fuel capture settings from the selected paidol plus pure utility
 * functions. Does NOT manage component state or auto-save handlers — those live
 * in FuelCaptureSection (desktop) so mobile only imports what it needs.
 */
export function useFuelCapture() {
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const fuelSettings = selectedPaidol?.fuelCaptureSettings as FuelCaptureToggleableSetting | null | undefined;

  const isFuelCaptureEnabled = fuelSettings?.isEnabled ?? false;
  const isVehicleIdRequired = fuelSettings?.isVehicleIdRequired ?? false;
  const isGallonsRequired = fuelSettings?.isGallonsRequired ?? false;
  const isOdometerRequired = fuelSettings?.isOdometerRequired ?? false;
  const isMCC5541Required = fuelSettings?.isMCC5541Required ?? false;
  const isMCC5542Required = fuelSettings?.isMCC5542Required ?? false;

  const activeFuelMCCs = useMemo(() => {
    const mccs: string[] = [];
    if (isMCC5541Required) mccs.push('5541');
    if (isMCC5542Required) mccs.push('5542');
    return mccs;
  }, [isMCC5541Required, isMCC5542Required]);

  const isFuelTransactionFn = useCallback(
    (mcc: string | null | undefined): boolean => !!mcc && activeFuelMCCs.includes(mcc),
    [activeFuelMCCs]
  );

  const checkMissingFields = useCallback(
    (params: CheckMissingFieldsParams): CheckMissingFieldsResult => {
      const isMatch = isFuelTransactionFn(params.merchantCategoryCode);
      return {
        isFuelTransaction: isMatch,
        missingVehicleId:
          isMatch && isVehicleIdRequired && (!params.vehicleId || params.vehicleId.trim() === ''),
        missingGallons: isMatch && isGallonsRequired && params.gallons == null,
        missingOdometer: isMatch && isOdometerRequired && params.odometerReading == null,
      };
    },
    [isFuelTransactionFn, isVehicleIdRequired, isGallonsRequired, isOdometerRequired]
  );

  return {
    // Settings
    isFuelCaptureEnabled,
    isVehicleIdRequired,
    isGallonsRequired,
    isOdometerRequired,
    isMCC5541Required,
    isMCC5542Required,

    // Utility functions
    isFuelTransactionFn,
    checkMissingFields,

    // Pure utilities re-exported for single-import convenience
    calculateCostPerGallon,
    detectIsManualCost,
    isTransactionFinalState,
  };
}

// ─── State hook ───────────────────────────────────────────────────────────────

export interface UseFuelCaptureStateOptions {
  transaction: HighnoteTransactionEvent | undefined;
  /**
   * Pass `open` when the parent is a persistent sheet that never unmounts.
   * The hook re-inits whenever `open` becomes true with a transaction.
   * Defaults to `true` (always active — component mounts/unmounts with the transaction).
   */
  open?: boolean;
}

/**
 * Manages fuel capture field state, init, and all derived values for a single
 * transaction. Shared between desktop (FuelCaptureSection) and mobile
 * (MobileEditTransactionSheet) so the logic lives in one place.
 */
export function useFuelCaptureState({ transaction, open = true }: UseFuelCaptureStateOptions) {
  const {
    isFuelCaptureEnabled,
    isVehicleIdRequired,
    isGallonsRequired,
    isOdometerRequired,
    isMCC5541Required,
    isMCC5542Required,
    isFuelTransactionFn,
    checkMissingFields,
  } = useFuelCapture();

  // ── State ──────────────────────────────────────────────────────────────────
  const [vehicleId, setVehicleId] = useState('');
  const [odometerReading, setOdometerReading] = useState<number | null>(null);
  const [gallons, setGallons] = useState<number | null>(null);
  const [manualCostPerGallon, setManualCostPerGallon] = useState<number | null>(null);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const transactionAmount = transaction?.approvedAmount?.value ?? 0;
  const isPending = transaction?.status === HighnoteTransactionStatus.APPROVED;

  const calculatedCostPerGallon = useMemo(
    () => calculateCostPerGallon(transactionAmount, gallons, isPending),
    [transactionAmount, gallons, isPending]
  );

  const displayCostPerGallon: number | null = manualCostPerGallon ?? calculatedCostPerGallon;
  const isManualCostPerGallon = manualCostPerGallon !== null;

  const isFuelTransaction = isFuelTransactionFn(transaction?.merchantCategoryCode);
  const isTransactionFinal = transaction ? isTransactionFinalState(transaction) : false;

  const missingVehicleId =
    isFuelTransaction && isVehicleIdRequired && (!vehicleId || vehicleId.trim() === '');
  const missingGallons = isFuelTransaction && isGallonsRequired && gallons == null;
  const missingOdometer = isFuelTransaction && isOdometerRequired && odometerReading == null;
  const isMissingFuelFields =
    isFuelCaptureEnabled &&
    isFuelTransaction &&
    !isTransactionFinal &&
    (missingVehicleId || missingGallons || missingOdometer);

  // ── Init from transaction ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !transaction) return;
    setVehicleId(transaction.fuelVehicleId || '');
    setOdometerReading(transaction.fuelOdometerReading ?? null);
    setGallons(transaction.fuelGallons ?? null);
    const txIsPending = transaction.status === HighnoteTransactionStatus.APPROVED;
    const isManual = detectIsManualCost(
      transaction.fuelCostPerGallon,
      transaction.approvedAmount?.value ?? 0,
      transaction.fuelGallons ?? null,
      txIsPending
    );
    setManualCostPerGallon(isManual ? transaction.fuelCostPerGallon ?? null : null);
  }, [transaction, open]);

  // ── Reset ──────────────────────────────────────────────────────────────────
  const resetFuelState = useCallback(() => {
    setVehicleId('');
    setOdometerReading(null);
    setGallons(null);
    setManualCostPerGallon(null);
  }, []);

  return {
    // State + setters
    vehicleId,
    setVehicleId,
    odometerReading,
    setOdometerReading,
    gallons,
    setGallons,
    manualCostPerGallon,
    setManualCostPerGallon,
    // Derived
    transactionAmount,
    isPending,
    calculatedCostPerGallon,
    displayCostPerGallon,
    isManualCostPerGallon,
    isFuelTransaction,
    isTransactionFinal,
    missingVehicleId,
    missingGallons,
    missingOdometer,
    isMissingFuelFields,
    // Reset
    resetFuelState,
    // Settings (re-exposed so callers only need one import)
    isFuelCaptureEnabled,
    isVehicleIdRequired,
    isGallonsRequired,
    isOdometerRequired,
    isMCC5541Required,
    isMCC5542Required,
    isFuelTransactionFn,
    checkMissingFields,
  };
}
